import jsPDF from 'jspdf';

interface OrderItem {
  product_name: string;
  product_price: number;
  quantity: number;
  selected_size?: string;
  selected_pot_type?: string;
  selected_pot_color?: string;
}

interface Order {
  id: string;
  order_number?: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  shipping_address: any;
  subtotal: number;
  shipping_cost: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  currency: string;
  payment_status: string;
  order_status: string;
  created_at: string;
  order_items: OrderItem[];
}

export const generateInvoice = (order: Order) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  let yPos = 20;

  // Use order_number if available, otherwise fallback to last 8 chars of UUID
  const orderDisplayId = order.order_number || order.id.slice(-8).toUpperCase();

  // Header
  doc.setFontSize(24);
  doc.setTextColor(40, 40, 40);
  doc.text('INVOICE', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 15;
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Invoice #${orderDisplayId}`, pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 5;
  doc.text(`Date: ${new Date(order.created_at).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })}`, pageWidth / 2, yPos, { align: 'center' });

  // Company Info (Left side)
  yPos += 20;
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text('From:', 15, yPos);
  
  yPos += 6;
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  doc.text('Paudhify', 15, yPos);
  yPos += 5;
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.text('2nd Floor, WeWork - ELDECO Centre,', 15, yPos);
  yPos += 4;
  doc.text('Block A, Shivalik Colony, Malviya Nagar,', 15, yPos);
  yPos += 4;
  doc.text('South Delhi, Delhi, India - 110017', 15, yPos);
  yPos += 4;
  doc.text('GSTIN: 07NZPPS8540L1Z4', 15, yPos);
  yPos += 4;
  doc.text('Email: info@paudhify.com', 15, yPos);

  // Customer Info (Right side)
  let rightYPos = yPos - 16;
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text('Bill To:', pageWidth - 15, rightYPos, { align: 'right' });
  
  rightYPos += 6;
  doc.setFontSize(10);
  doc.text(order.customer_name, pageWidth - 15, rightYPos, { align: 'right' });
  rightYPos += 5;
  doc.setTextColor(100, 100, 100);
  doc.text(order.customer_email, pageWidth - 15, rightYPos, { align: 'right' });
  
  if (order.customer_phone) {
    rightYPos += 5;
    doc.text(order.customer_phone, pageWidth - 15, rightYPos, { align: 'right' });
  }

  // Shipping Address
  if (order.shipping_address) {
    rightYPos += 5;
    const address = order.shipping_address;
    if (address.address) doc.text(address.address, pageWidth - 15, rightYPos, { align: 'right' });
    rightYPos += 5;
    if (address.city && address.state) {
      doc.text(`${address.city}, ${address.state} ${address.zipCode || address.zip_code || ''}`, pageWidth - 15, rightYPos, { align: 'right' });
    }
  }

  yPos = Math.max(yPos, rightYPos) + 15;

  // Order Items Table Header
  doc.setFillColor(240, 240, 240);
  doc.rect(15, yPos, pageWidth - 30, 8, 'F');
  
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  doc.text('Item', 20, yPos + 5);
  doc.text('Qty', pageWidth - 70, yPos + 5);
  doc.text('Price', pageWidth - 45, yPos + 5);
  doc.text('Total', pageWidth - 20, yPos + 5, { align: 'right' });

  yPos += 12;

  // Order Items
  order.order_items.forEach((item) => {
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    
    let itemName = item.product_name;
    const variantParts = [];
    if (item.selected_size) variantParts.push(item.selected_size);
    if (item.selected_pot_type) variantParts.push(item.selected_pot_type);
    if (item.selected_pot_color) variantParts.push(item.selected_pot_color);
    if (variantParts.length > 0) {
      itemName += ` (${variantParts.join(' / ')})`;
    }
    
    doc.text(itemName, 20, yPos);
    doc.text(item.quantity.toString(), pageWidth - 70, yPos);
    doc.text(`₹${item.product_price.toFixed(2)}`, pageWidth - 45, yPos);
    doc.text(`₹${(item.product_price * item.quantity).toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
    
    yPos += 7;
  });

  yPos += 5;
  doc.setDrawColor(200, 200, 200);
  doc.line(15, yPos, pageWidth - 15, yPos);
  yPos += 10;

  // Totals
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  
  doc.text('Subtotal:', pageWidth - 70, yPos);
  doc.text(`₹${order.subtotal.toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
  yPos += 6;

  if (order.discount_amount > 0) {
    doc.text('Discount:', pageWidth - 70, yPos);
    doc.text(`-₹${order.discount_amount.toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
    yPos += 6;
  }

  doc.text('Shipping:', pageWidth - 70, yPos);
  doc.text(`₹${order.shipping_cost.toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
  yPos += 6;

  doc.text('Tax:', pageWidth - 70, yPos);
  doc.text(`₹${order.tax_amount.toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
  yPos += 8;

  // Total
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.setFillColor(240, 240, 240);
  doc.rect(pageWidth - 90, yPos - 4, 75, 10, 'F');
  doc.text('Total:', pageWidth - 70, yPos + 2);
  doc.text(`₹${order.total_amount.toFixed(2)}`, pageWidth - 20, yPos + 2, { align: 'right' });

  // Payment Status
  yPos += 20;
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Payment Status: ${order.payment_status.toUpperCase()}`, pageWidth / 2, yPos, { align: 'center' });

  // Footer
  yPos = doc.internal.pageSize.height - 20;
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('Thank you for your purchase!', pageWidth / 2, yPos, { align: 'center' });
  yPos += 4;
  doc.text('For any queries, contact us at info@paudhify.com', pageWidth / 2, yPos, { align: 'center' });

  // Save PDF - use order_number in filename if available
  const fileName = `Invoice_${orderDisplayId}_${new Date(order.created_at).toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
